import type { ReactNode } from 'react'
import {
  BookOpen,
  Layers,
  PieChart,
  Users,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  DashboardChartCard,
  DashboardMetricCard,
  DashboardMetricGrid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@yeshuman/sdk'
import type { PluginCanvasGlanceProps, PluginCanvasProps } from '@yeshuman/sdk'

import { useSkillPoolCoverage, type PoolCoverageReport } from '../../hooks/use-skill-pool-coverage'

function formatPercent(ratio: number): string {
  return `${(ratio * 100).toFixed(1)}%`
}

function SkillLexiconLink({ children }: { children: string }) {
  return (
    <Link to="/platform/skills-catalog" className="text-primary underline-offset-4 hover:underline">
      {children}
    </Link>
  )
}

function FullReportLink({ pagePath }: { pagePath: string | null }) {
  if (!pagePath) return null
  return (
    <Link to={pagePath} className="text-sm font-medium text-primary underline-offset-4 hover:underline">
      Full coverage report
    </Link>
  )
}

function DomainBars({ report }: { report: PoolCoverageReport }) {
  if (report.empty_catalog) {
    return (
      <p className="text-sm text-muted-foreground">
        No published domains yet. {report.message}{' '}
        <SkillLexiconLink>Open Skill Lexicon</SkillLexiconLink>
      </p>
    )
  }
  const max = Math.max(1, ...report.domains.map((domain) => domain.candidate_count))
  return (
    <ul className="space-y-3">
      {report.domains.map((domain) => (
        <li key={domain.id} className="flex items-center gap-3">
          <span className="w-40 shrink-0 truncate text-sm">{domain.label}</span>
          <div className="h-2 flex-1 overflow-hidden rounded bg-muted">
            <div
              className="h-2 rounded bg-primary"
              style={{ width: `${(domain.candidate_count / max) * 100}%` }}
            />
          </div>
          <span className="w-10 shrink-0 text-right text-sm tabular-nums">
            {domain.candidate_count}
          </span>
        </li>
      ))}
    </ul>
  )
}

function SkillTable({
  rows,
  empty,
  showExperience,
}: {
  rows: PoolCoverageReport['most_represented']
  empty: string
  showExperience?: boolean
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">{empty}</p>
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Skill</TableHead>
          <TableHead className="text-right">Profiles</TableHead>
          {showExperience ? (
            <TableHead className="text-right">Experience months</TableHead>
          ) : (
            <TableHead className="text-right">Pool rank</TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell>{row.name}</TableCell>
            <TableCell className="text-right tabular-nums">{row.candidate_count}</TableCell>
            <TableCell className="text-right tabular-nums">
              {showExperience ? row.experience_months : row.pool_rank}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function CoverageStatus({
  isLoading,
  error,
  children,
}: {
  isLoading: boolean
  error: Error | null
  children: ReactNode
}) {
  const status = (error as (Error & { status?: number }) | null)?.status
  const forbidden = status === 403 || status === 401
  if (forbidden) {
    return (
      <p className="text-sm text-muted-foreground">
        Platform operators only. Switch to the platform focus to view this report.
      </p>
    )
  }
  if (error) {
    return <p className="text-sm text-destructive">{error.message}</p>
  }
  if (!isLoading && !children) return null
  return <>{children}</>
}

function CoverageMetrics({
  data,
  isLoading,
  variant,
}: {
  data?: PoolCoverageReport
  isLoading: boolean
  variant?: 'plain'
}) {
  return (
    <DashboardMetricGrid maxColumns={4} variant={variant}>
      <DashboardMetricCard
        title="Coverage"
        value={isLoading || !data ? '—' : formatPercent(data.coverage_ratio)}
        description={
          data ? `${data.covered_profile_count} of ${data.profile_count} profiles` : undefined
        }
        icon={Users}
        loading={isLoading}
      />
      <DashboardMetricCard
        title="Catalog use"
        value={isLoading || !data ? '—' : formatPercent(data.catalog_use_ratio)}
        description={
          data
            ? `${data.possessed_published_skills} of ${data.published_skills} published skills`
            : undefined
        }
        icon={BookOpen}
        loading={isLoading}
      />
      <DashboardMetricCard
        title="Concentration"
        value={isLoading || !data ? '—' : formatPercent(data.concentration_ratio)}
        description="Share of possessions in the top 10 published skills"
        icon={Layers}
        loading={isLoading}
      />
      <DashboardMetricCard
        title="Depth"
        value={isLoading || !data ? '—' : formatPercent(data.depth_ratio)}
        description="Advanced and Expert share of proficiency assignments"
        icon={PieChart}
        loading={isLoading}
      />
    </DashboardMetricGrid>
  )
}

export function SkillPoolGlance({ pagePath }: PluginCanvasGlanceProps) {
  const { data, isLoading, error } = useSkillPoolCoverage()

  return (
    <section className="space-y-4" aria-label="Skill pool">
      <CoverageStatus isLoading={isLoading} error={error as Error | null}>
        <CoverageMetrics data={data} isLoading={isLoading} variant="plain" />
        {data?.message ? (
          <p className="text-sm text-muted-foreground">
            {data.message}{' '}
            {data.empty_catalog ? <SkillLexiconLink>Open Skill Lexicon</SkillLexiconLink> : null}
          </p>
        ) : null}
        {data ? <DomainBars report={data} /> : null}
      </CoverageStatus>
      <FullReportLink pagePath={pagePath} />
    </section>
  )
}

export function SkillPoolCoverageReport({ mode, pagePath }: PluginCanvasProps) {
  const { data, isLoading, error } = useSkillPoolCoverage()

  return (
    <div className="space-y-6">
      {mode === 'canvas' ? <FullReportLink pagePath={pagePath} /> : (
        <p className="text-sm text-muted-foreground">
          <SkillLexiconLink>Skill Lexicon</SkillLexiconLink>
        </p>
      )}

      <CoverageStatus isLoading={isLoading} error={error as Error | null}>
        <>
          <CoverageMetrics data={data} isLoading={isLoading} />

          {data?.message ? (
            <p className="text-sm text-muted-foreground">
              {data.message}{' '}
              {data.empty_catalog ? <SkillLexiconLink>Open Skill Lexicon</SkillLexiconLink> : null}
            </p>
          ) : null}

          <DashboardChartCard
            title="Domain coverage"
            description="Distinct profiles with at least one published skill in each published domain."
            loading={isLoading}
          >
            {data ? <DomainBars report={data} /> : null}
          </DashboardChartCard>

          <DashboardChartCard
            title="Most represented"
            description="Published skills ordered by how many profiles possess them."
            loading={isLoading}
          >
            {data ? (
              <SkillTable
                rows={data.most_represented}
                empty="No published skills are possessed yet."
              />
            ) : null}
          </DashboardChartCard>

          <DashboardChartCard
            title="Most experience"
            description="Unique experience months linked to each published skill. Possession-only skills show 0."
            loading={isLoading}
          >
            {data ? (
              <SkillTable
                rows={data.most_experience.filter((row) => row.experience_months > 0)}
                empty="No published skills have linked experience months yet. Possession-only skills show 0 and are omitted here."
                showExperience
              />
            ) : null}
          </DashboardChartCard>

          <DashboardChartCard
            title="Coverage of the base"
            description="Profiles without a published catalog skill, plus the long tail of unused published skills."
            loading={isLoading}
          >
            {data ? (
              <div className="space-y-4">
                <p className="text-sm">
                  Uncovered profiles:{' '}
                  <span className="font-medium tabular-nums">{data.uncovered_profile_count}</span>
                  {' · '}
                  Recency:{' '}
                  {`Current ${data.recency_mix.Current}, Recent ${data.recency_mix.Recent}, Not recent ${data.recency_mix['Not recent']}, Undated ${data.recency_mix.Undated}`}
                </p>
                <SkillTable
                  rows={data.long_tail.map((row) => ({
                    ...row,
                    pool_rank: 0,
                    proficiency_mix: {
                      Foundational: 0,
                      Working: 0,
                      Advanced: 0,
                      Expert: 0,
                    },
                    experience_months: 0,
                  }))}
                  empty="Every published skill is possessed by at least one profile."
                />
              </div>
            ) : null}
          </DashboardChartCard>
        </>
      </CoverageStatus>
    </div>
  )
}
